import get from 'lodash/get';
import identity from 'lodash/identity';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import set from 'lodash/set';

import { toArray } from './Array';
import { isString, isBlank } from './String';

const DEFAULT_OBJECT = {};

const isObject = (data) =>
  data !== null && (typeof data === 'object' || typeof data === 'function');

const isEmpty = (data) => isObject(data) && !Object.keys(data).length;

function setEmptyPropertyToNull(data = {}) {
  const keys = Object.keys(data);

  keys.forEach((key) => {
    if (data[key] === '') {
      data[key] = null;
    } else if (isObject(data[key])) {
      data[key] = setEmptyPropertyToNull(data[key]);
    }
  });

  return data;
}

function allHasSameProp(value, prop, items) {
  return items.every((item) => item[prop] === value);
}

function createSelectOptions(values = [], labels = [], keys = []) {
  if (values.length !== labels.length) {
    return [];
  }

  return values.map((value, idx) => ({
    value,
    label: labels[idx],
    ...(keys[idx] && { key: keys[idx] })
  }));
}

const mergeObjects = (acc, item) => ({ ...acc, ...item });

const compareProps = (watchedProps) => (prevProps, props) =>
  watchedProps.every((propPath) =>
    isEqual(get(prevProps, propPath), get(props, propPath))
  );

/**
 * @function
 * Converts one data structure from origin data structure to destination and
 * vice versa.
 * @param {object} mapper - maps the property name/path to destination name/path
 * @example
 * ```
 * const converter = Converter([
 *  'keyA',
 *  {
 *    origin: 'keyB',
 *    destination: 'newKeyB',
 *  },
 *  {
 *    origin: 'keyC',
 *    destination: 'newKeyC',
 *    toOrigin: {
 *      conditions: ({ newKeyA, newKeyB, newKeyC, newKeyD }) => true
 *    },
 *    toDestination: {
 *      conditions: ({ keyA, keyB, keyC, keyD }) => true
 *    }
 *  },
 *  {
 *    origin: 'keyD',
 *    destination: 'newKeyD',
 *    toOrigin: {
 *      tranform: valueD => parseInt(valueD, 10)
 *    },
 *    toDestination: {
 *      tranform: newValueD => `${newValueD}`
 *    }
 *  }
 * ]);
 * const originObject = {
 *  'keyA': 'valueA',
 *  'keyB': 'valueB',
 *  'keyC': 'valueC',
 *  'keyD': 10,
 * };
 * converter.asDestinationObject(originObject);
 * // {
 * // 'keyA': 'valueA',
 * // 'newKeyB': 'valueB',
 * // 'newKeyC': 'valueC',
 * // 'newKeyD': '10',
 * //}
 * ```
 */
function Converter(mapper) {
  const mapperAsArray = toArray(mapper);

  function checkConditions(conditions, obj) {
    return toArray(conditions).some((condition) => !condition(obj));
  }

  function originToDestinationTransformer(originObject) {
    return mapperAsArray.reduce((obj, entry) => {
      if (isString(entry)) {
        return set(obj, entry, get(originObject, entry, null));
      } else if (isObject(entry)) {
        const { origin, destination, toDestination } = entry;

        if (isBlank(origin) || isBlank(destination)) {
          return obj;
        }

        const { transform = identity, conditions } =
          toDestination || DEFAULT_OBJECT;

        if (checkConditions(conditions, originObject)) {
          return obj;
        }

        const transformedValue = transform(get(originObject, origin, null));

        return set(obj, destination, transformedValue);
      }

      return obj;
    }, {});
  }

  function destinationToOriginTransformer(destinationObject) {
    return mapperAsArray.reduce((obj, entry) => {
      if (isString(entry)) {
        return set(obj, entry, get(destinationObject, entry, null));
      } else if (isObject(entry)) {
        const { origin, destination, toOrigin } = entry;

        if (isBlank(origin) || isBlank(destination)) {
          return obj;
        }

        const { transform = identity, conditions } = toOrigin || DEFAULT_OBJECT;

        if (checkConditions(conditions, destinationObject)) {
          return obj;
        }

        const transformedValue = transform(
          get(destinationObject, destination, null)
        );

        return set(obj, origin, transformedValue);
      }

      return obj;
    }, {});
  }

  return {
    asDestinationObject(originObject, append = DEFAULT_OBJECT) {
      const destinationObject = originToDestinationTransformer(originObject);

      return { ...destinationObject, ...append };
    },
    asOriginObject(destinationObject, append = DEFAULT_OBJECT) {
      const originObject = destinationToOriginTransformer(destinationObject);

      return { ...originObject, ...append };
    }
  };
}

export {
  allHasSameProp,
  createSelectOptions,
  get,
  isEmpty,
  isObject,
  setEmptyPropertyToNull,
  mergeObjects,
  Converter,
  pick,
  compareProps
};
